import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { REVIEWS, SITE } from "../config/site";

const Counter = ({ to, suffix = "", decimals = 0, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} data-testid={testid}>
      {val.toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
};

const STATS = [
  { to: 4.5, decimals: 1, suffix: "★", label: "Google Rating", testid: "counter-rating" },
  { to: 197, suffix: "+", label: "Happy Customers", testid: "counter-customers" },
  { to: 19, suffix: "+", label: "Signature Services", testid: "counter-services" },
  { to: 7, suffix: "", label: "Days Open Weekly", testid: "counter-days" },
];

const Stars = () => (
  <div className="flex gap-1" aria-label="5 star rating">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} className="text-brand-gold fill-brand-gold" aria-hidden="true" />
    ))}
  </div>
);

export const Reviews = () => {
  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 sm:py-32 bg-brand-green relative overflow-hidden">
      <div
        className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-brand-gold/10 blur-3xl"
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Overline light>Word of Mouth</Overline>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight">
            Loved by
            <span className="italic text-brand-goldlight"> thousands</span> in
            the neighbourhood.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <Reveal key={s.label}>
              <div className="border-l border-white/20 pl-5">
                <p className="font-serif text-4xl sm:text-5xl font-bold text-white">
                  <Counter to={s.to} suffix={s.suffix} decimals={s.decimals || 0} testid={s.testid} />
                </p>
                <p className="mt-2 text-white/60 text-sm tracking-wide">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.1}>
              <blockquote
                data-testid={`review-card-${i}`}
                className="h-full bg-brand-cream rounded-3xl p-8 shadow-lift flex flex-col"
              >
                <Quote size={28} className="text-brand-gold/50" aria-hidden="true" />
                <div className="mt-4">
                  <Stars />
                </div>
                <p className="mt-4 text-brand-ink leading-relaxed grow">
                  "{r.quote}"
                </p>
                <footer className="mt-6 pt-5 border-t border-brand-green/10">
                  <p className="font-serif text-lg font-semibold text-brand-green">
                    – {r.name}
                  </p>
                  <p className="text-xs text-brand-mute mt-1 tracking-wide">
                    Source: {r.source}
                  </p>
                </footer>
              </blockquote>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <a
            data-testid="view-more-reviews-button"
            href={SITE.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-12 inline-flex items-center gap-2 rounded-full bg-brand-gold px-8 py-4 text-sm sm:text-base font-semibold text-white shadow-gold transition-all duration-300 hover:bg-brand-cream hover:text-brand-green hover:scale-[1.03]"
          >
            View More Google Reviews
            <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
